import { FC } from 'react';
import {
	Link,
	useLocation,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import { Box, Flex, HStack, ListIcon, ListItem, Text } from 'src/components/ui';
import 'src/styles/hover.css';
import { IMenuItem } from 'src/services/menu';
import { iconMapper } from 'src/services/iconMapper';

interface NavListItemProps extends IMenuItem {
	shouldRefresh?: boolean;
	onItemClick?: () => void;
}

/**
 * Generate a menu ID from the URL and label for tour targeting
 */
const getMenuId = (url: string | undefined, label: string): string | undefined => {
	// First try URL mapping
	if (url) {
		const urlToIdMap: Record<string, string> = {
			'/home': 'menu-home',
			'/projects/campaigns': 'menu-campaigns',
			'/tasks': 'menu-tasks',
			'/account': 'menu-account',
			'/commerce/catalog': 'menu-commerce',
			'/integrations': 'menu-integrations',
			'/ai-voice-agent': 'menu-voice-agent',
		};

		if (urlToIdMap[url]) {
			return urlToIdMap[url];
		}
	}

	// Fallback to label mapping (case-insensitive)
	const normalizedLabel = label.toLowerCase().trim();
	const labelToIdMap: Record<string, string> = {
		'home': 'menu-home',
		'ad campaigns': 'menu-campaigns',
		'campaigns': 'menu-campaigns',
		'tasks': 'menu-tasks',
		'quick tools': 'menu-tasks',
		'account': 'menu-account',
		'commerce': 'menu-commerce',
		'integrations': 'menu-integrations',
		'ai voice agent': 'menu-voice-agent',
	};

	return labelToIdMap[normalizedLabel];
};

const NavListItem: FC<NavListItemProps> = ({
	label,
	icon,
	url,
	shouldRefresh,
	onItemClick,
}) => {
	const { pathname } = useLocation();
	const [search] = useSearchParams();
	const navigate = useNavigate();

	const handleClick = () => {
		navigate(url ?? '');
		onItemClick?.();
	};

	const isActiveLink = (link: string) => {
		const [baseLink, searchParam] = link.split('?');
		const isActivePath = pathname.startsWith(baseLink);

		if (searchParam) {
			return isActivePath && search.toString() === searchParam;
		}
		return isActivePath;
	};

	const activeLinkProps = isActiveLink(url ?? '')
		? { color: 'orange.base', bg: '#EDEEEF', borderLeftRadius: 0 }
		: {};

	const menuId = getMenuId(url, label);

	return (
		<Flex>
			<Box
				borderRight={
					isActiveLink(url ?? '')
						? '3px solid #F7480B'
						: '3px solid transparent'
				}
				borderRadius="0px 4px 4px 0px"
			/>
			<Box w="full">
				<ListItem
					cursor="pointer"
					borderRadius="6px"
					onClick={handleClick}
				>
					<HStack
						id={menuId}
						className="container"
						_hover={{
							color: 'orange.base',
						}}
						p={3}
						pl="20px"
						borderRadius="6px"
						{...activeLinkProps}
					>
						{icon && (
							<ListIcon
								as={iconMapper[icon as keyof typeof iconMapper] ?? (() => null)}
							/>
						)}
						<Text
							className="text"
							fontWeight="500"
							lineHeight="175%"
							letterSpacing="0.2px"
							{...activeLinkProps}
						>
							{label}
						</Text>
					</HStack>
				</ListItem>
			</Box>
		</Flex>
	);
};

export default NavListItem;
